import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { auth } from '../firebase/config';
import axios from 'axios';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '../utils/countryCodes';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create axios client with optional auth
const createApiClient = async (includeAuth = false) => {
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (includeAuth && auth.currentUser) {
    const token = await auth.currentUser.getIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return axios.create({
    baseURL: API_URL,
    headers
  });
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [guestInfo, setGuestInfo] = useState({ name: '', phone: '', countryCode: DEFAULT_COUNTRY_CODE });
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestInfoCollected, setGuestInfoCollected] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(DEFAULT_COUNTRY_CODE);
  const messagesEndRef = useRef(null);
  const { user } = useAuth();
  const location = useLocation();

  // Don't show chatbot/WhatsApp on bio pages
  const isBioPage = location.pathname.startsWith('/bio/');
  if (isBioPage) {
    return null;
  }

  // Check if user is admin/reviewer
  const isAdminOrReviewer = user && (
    user.email === 'sagarshiva111555@gmail.com' || 
    user.email === 'allgrowtechonologies@gmail.com'
  );

  // Initialize session
  useEffect(() => {
    // Get or create session ID from localStorage
    const storedSessionId = localStorage.getItem('chatbot_session_id');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      // Load history for this session
      loadChatHistory(storedSessionId);
    }
    
    // Load navigation history from localStorage
    const storedNavHistory = localStorage.getItem('chatbot_nav_history');
    if (storedNavHistory) {
      try {
        setNavigationHistory(JSON.parse(storedNavHistory));
      } catch (e) {
        console.error('Failed to parse navigation history:', e);
      }
    }
    
    // Load guest info from localStorage
    const storedGuestInfo = localStorage.getItem('chatbot_guest_info');
    if (storedGuestInfo) {
      try {
        const info = JSON.parse(storedGuestInfo);
        setGuestInfo(info);
        if (info.name && info.phone) {
          setGuestInfoCollected(true);
        }
      } catch (e) {
        console.error('Failed to parse guest info:', e);
      }
    }
    
    // Detect user's country based on IP using BigDataCloud (CORS-friendly)
    const detectCountry = async () => {
      try {
        const response = await fetch('https://api.bigdatacloud.net/data/reverse-geocode-client');
        const data = await response.json();
        // BigDataCloud returns country info, map to calling code
        const countryName = data.countryName;
        const countryCodeMap = {
          'India': '+91', 'United States': '+1', 'United Kingdom': '+44',
          'Canada': '+1', 'Australia': '+61', 'Germany': '+49',
          'France': '+33', 'Japan': '+81', 'China': '+86'
        };
        const countryCode = countryCodeMap[countryName] || '+91';
        const found = COUNTRY_CODES.find(c => c.code === countryCode);
        if (found) {
          setDetectedCountry(countryCode);
          setGuestInfo(prev => ({ ...prev, countryCode: countryCode }));
        }
      } catch (e) {
        console.log('Could not detect country, defaulting to +91');
      }
    };
    detectCountry();
  }, []);

  // Track navigation changes - always track, even before chat starts
  useEffect(() => {
    const currentPath = location.pathname;
    
    setNavigationHistory(prev => {
      // Add current path if it's not the last item
      if (prev.length === 0 || prev[prev.length - 1] !== currentPath) {
        const newHistory = [...prev, currentPath];
        // Keep only last 20 pages
        const trimmed = newHistory.slice(-20);
        // Save to localStorage
        localStorage.setItem('chatbot_nav_history', JSON.stringify(trimmed));
        console.log('📍 Navigation tracked:', currentPath, '| History:', trimmed.length, 'pages');
        return trimmed;
      }
      return prev;
    });
  }, [location.pathname]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle opening chat - show guest form if not logged in and info not collected
  const handleOpenChat = () => {
    setIsOpen(true);
    if (!user && !guestInfoCollected) {
      setShowGuestForm(true);
    }
  };

  // Handle guest info submission
  const handleGuestInfoSubmit = (e) => {
    e.preventDefault();
    if (guestInfo.name.trim() && guestInfo.phone.trim()) {
      // Validate phone number (basic validation - at least 6 digits for some countries)
      const phoneDigits = guestInfo.phone.replace(/\D/g, '');
      if (phoneDigits.length < 6) {
        alert('Please enter a valid phone number');
        return;
      }
      
      // Create full phone number with country code
      const fullPhoneNumber = `${guestInfo.countryCode} ${guestInfo.phone}`;
      const guestInfoToSave = {
        ...guestInfo,
        phone: fullPhoneNumber
      };
      
      // Save guest info
      localStorage.setItem('chatbot_guest_info', JSON.stringify(guestInfoToSave));
      setGuestInfo(guestInfoToSave);
      setGuestInfoCollected(true);
      setShowGuestForm(false);
      
      // Add welcome message
      const welcomeMessage = {
        type: 'bot',
        text: `Hi ${guestInfo.name}! 👋 Thanks for sharing your details. How can I help you today?`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, welcomeMessage]);
    }
  };

  const handleCloseChat = async () => {
    // Close the chat window
    setIsOpen(false);
    
    // If there's an active session, mark it as closed
    if (sessionId && messages.length > 0) {
      try {
        const apiClient = await createApiClient(false);
        await apiClient.post('/api/admin/chat-support/close-chat', null, {
          params: { session_id: sessionId }
        });
        console.log('✅ Chat session marked as closed');
      } catch (error) {
        console.error('Failed to mark chat as closed:', error);
        // Don't show error to user, just log it
      }
    }
  };

  const loadChatHistory = async (sid) => {
    try {
      const apiClient = await createApiClient(false); // No auth needed for history
      const response = await apiClient.get(`/api/chatbot/history/${sid}`);
      const history = response.data;
      
      // Convert to message format
      const formattedMessages = [];
      history.forEach(msg => {
        formattedMessages.push({
          type: 'user',
          text: msg.user_message,
          timestamp: msg.timestamp
        });
        formattedMessages.push({
          type: 'bot',
          text: msg.bot_response,
          timestamp: msg.timestamp
        });
      });
      
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    // For guest users, ensure info is collected
    if (!user && !guestInfoCollected) {
      setShowGuestForm(true);
      return;
    }

    const userMessage = inputMessage;
    setInputMessage('');

    // Add user message to UI
    const newUserMessage = {
      type: 'user',
      text: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const apiClient = await createApiClient(!!user); // Include auth if user is logged in
      
      // Prepare message data with navigation info and guest details
      const messageData = {
        message: userMessage,
        session_id: sessionId,
        current_page: location.pathname,
        navigation_history: navigationHistory,
        user_name: user?.displayName || user?.email?.split('@')[0] || guestInfo.name || 'Guest',
        phone_number: guestInfo.phone || null
      };
      
      console.log('📤 Sending message with navigation:', {
        current_page: messageData.current_page,
        nav_history_count: messageData.navigation_history.length,
        user_name: messageData.user_name,
        has_phone: !!messageData.phone_number
      });
      
      const response = await apiClient.post('/api/chatbot/chat', messageData);

      const botResponse = response.data.response;
      const newSessionId = response.data.session_id;

      // Store session ID
      if (!sessionId) {
        setSessionId(newSessionId);
        localStorage.setItem('chatbot_session_id', newSessionId);
      }

      // Add bot message to UI
      const newBotMessage = {
        type: 'bot',
        text: botResponse,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, newBotMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message
      const errorMessage = {
        type: 'bot',
        text: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    const newSessionId = `session_${Date.now()}`;
    setSessionId(newSessionId);
    localStorage.setItem('chatbot_session_id', newSessionId);
    setMessages([]);
  };

  const escalateToHumanSupport = async () => {
    setShowMenu(false);
    
    // Add user message
    const userMessage = {
      type: 'user',
      text: 'I need human support',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const apiClient = await createApiClient(true); // Always use auth for escalation
      const response = await apiClient.post('/api/chatbot/chat', {
        message: 'human support',
        session_id: sessionId
      });

      const botResponse = response.data.response;
      const newSessionId = response.data.session_id;

      if (!sessionId) {
        setSessionId(newSessionId);
        localStorage.setItem('chatbot_session_id', newSessionId);
      }

      const newBotMessage = {
        type: 'bot',
        text: botResponse,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, newBotMessage]);
    } catch (error) {
      console.error('Error escalating to human support:', error);
      
      const errorMessage = {
        type: 'bot',
        text: 'Sorry, I encountered an error escalating your request. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* WhatsApp Button for Guest Users */}
      {!isOpen && !user && (
        <a
          href="https://wa.me/919281415554?text=Hi%2C%20I%20have%20a%20question%20about%20Piper%20Card"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 w-14 h-14 sm:w-16 sm:h-16 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-50"
          aria-label="Chat on WhatsApp"
        >
          <svg className="w-7 h-7 sm:w-8 sm:h-8" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      )}

      {/* Floating Chat Button - Only for logged in users */}
      {!isOpen && user && (
        <button
          onClick={handleOpenChat}
          className="fixed bottom-6 right-6 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-50 animate-pulse hover:animate-none"
          aria-label="Open chat"
        >
          <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8" />
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-bounce">
            AI
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-[90vw] sm:w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border-2 border-amber-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-600 to-yellow-500 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <div>
                <h3 className="font-bold text-lg">Piper Card Assistant</h3>
                <p className="text-xs text-amber-100">Powered by Gemini AI</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Human Support Menu for Admin/Reviewer */}
              {isAdminOrReviewer && (
                <div className="relative">
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="hover:bg-white/20 p-2 rounded-lg transition-colors flex items-center gap-1"
                    title="Menu"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-10">
                      <div className="py-2">
                        <button
                          onClick={escalateToHumanSupport}
                          className="w-full text-left px-4 py-3 hover:bg-amber-50 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                          </svg>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">Human Support</p>
                            <p className="text-xs text-gray-500">Escalate to support team</p>
                          </div>
                        </button>
                        
                        <button
                          onClick={startNewChat}
                          className="w-full text-left px-4 py-3 hover:bg-amber-50 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">New Chat</p>
                            <p className="text-xs text-gray-500">Start fresh conversation</p>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <button
                onClick={handleCloseChat}
                className="hover:bg-white/20 p-1 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {/* Guest Info Collection Form */}
            {!user && showGuestForm && !guestInfoCollected && (
              <div className="bg-white rounded-xl p-5 border border-amber-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-6 h-6 text-amber-500" />
                  <h4 className="font-semibold text-gray-800">Welcome! Let&apos;s get started</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Please share your details so our team can follow up if needed.
                </p>
                <form onSubmit={handleGuestInfoSubmit} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Your Name *</label>
                    <input
                      type="text"
                      value={guestInfo.name}
                      onChange={(e) => setGuestInfo(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter your name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number *</label>
                    <div className="flex gap-2">
                      <select
                        value={guestInfo.countryCode}
                        onChange={(e) => setGuestInfo(prev => ({ ...prev, countryCode: e.target.value }))}
                        className="w-28 px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm bg-white"
                      >
                        {COUNTRY_CODES.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        value={guestInfo.phone}
                        onChange={(e) => setGuestInfo(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, '') }))}
                        placeholder="Phone number"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white py-2 rounded-lg font-medium text-sm transition-all"
                  >
                    Start Chatting
                  </button>
                </form>
                <p className="text-xs text-gray-400 mt-3 text-center">
                  Your info helps our admin team assist you better
                </p>
              </div>
            )}

            {/* Welcome message for logged in users or after guest info collected */}
            {(user || guestInfoCollected) && messages.length === 0 && (
              <div className="text-center py-8">
                <Sparkles className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <p className="text-gray-600 text-sm">
                  Hi{user ? ` ${user.displayName || 'there'}` : guestInfo.name ? ` ${guestInfo.name}` : ''}! I&apos;m your Piper Card assistant.
                </p>
                <p className="text-gray-500 text-xs mt-2">
                  Ask me anything about our platform!
                </p>
              </div>
            )}

            {/* Show initial prompt for guests who haven't submitted info yet */}
            {!user && !showGuestForm && !guestInfoCollected && messages.length === 0 && (
              <div className="text-center py-8">
                <Sparkles className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <p className="text-gray-600 text-sm">
                  Hi! I&apos;m your Piper Card assistant.
                </p>
                <p className="text-gray-500 text-xs mt-2">
                  Please share your details to start chatting!
                </p>
              </div>
            )}

            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.type === 'user'
                      ? 'bg-gradient-to-r from-amber-600 to-yellow-500 text-white'
                      : msg.isError
                      ? 'bg-red-100 text-red-800'
                      : 'bg-white text-gray-800 border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                  <p className={`text-xs mt-1 ${
                    msg.type === 'user' ? 'text-amber-100' : 'text-gray-400'
                  }`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 border border-gray-200 rounded-2xl px-4 py-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={!user && !guestInfoCollected ? "Please share your details first..." : "Type your message..."}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                disabled={isLoading || (!user && !guestInfoCollected)}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputMessage.trim() || (!user && !guestInfoCollected)}
                className="bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white p-2 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            
            {/* Helper Text */}
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">
                {user ? (
                  <>
                    {isAdminOrReviewer && <span className="font-semibold text-amber-600">Admin • </span>}
                    {user.email}
                  </>
                ) : guestInfoCollected ? (
                  <>
                    <span className="font-medium text-gray-700">{guestInfo.name}</span>
                    <span className="mx-1">•</span>
                    <span>{guestInfo.phone}</span>
                  </>
                ) : (
                  'Please enter your details to start'
                )}
              </p>
              {!isAdminOrReviewer && (user || guestInfoCollected) && (
                <button
                  onClick={startNewChat}
                  className="text-xs text-amber-600 hover:text-amber-800 font-semibold"
                >
                  New Chat
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
